import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import supabase from '../supabase/supabase';
import { format, subDays } from 'date-fns';
import { getDefaultDailyData } from '../utils/storage';

const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [watchlists, setWatchlists] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentDaily, setCurrentDaily] = useState(getDefaultDailyData());
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const dailyDataRef = useRef(currentDaily);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) fetchProfile(session.user.id);
      else setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) fetchProfile(session.user.id);
      else {
        setProfile(null);
        setWatchlists(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('profiles_20240520')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;
      setProfile(data);
      fetchWatchlists();
    } catch (err) {
      console.error('Profile Fetch Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchWatchlists = async () => {
    const { data } = await supabase
      .from('watchlists_20240520')
      .select('*')
      .order('created_at', { ascending: true });
    setWatchlists(data || []);
  };

  const saveWatchlist = async (watchlist) => {
    if (!session?.user?.id) return;
    setIsSaving(true);
    try {
      const { data, error } = await supabase
        .from('watchlists_20240520')
        .upsert({ ...watchlist, user_id: session.user.id })
        .select()
        .single();

      if (error) throw error;
      setWatchlists(prev => {
        const exists = prev.find(w => w.id === data.id);
        if (exists) return prev.map(w => w.id === data.id ? data : w);
        return [...prev, data];
      });
      return data;
    } catch (err) {
      console.error("Error saving watchlist:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const moveInstrument = async (instrument, fromWatchlistId, toWatchlistId) => {
    if (fromWatchlistId === toWatchlistId) return;
    setIsSaving(true);
    try {
      const fromList = watchlists.find(w => w.id === fromWatchlistId);
      const toList = watchlists.find(w => w.id === toWatchlistId);
      if (!fromList || !toList) return;

      const updatedFromInstruments = fromList.instruments.filter(i => i.id !== instrument.id);
      const updatedToInstruments = [...toList.instruments, instrument];

      // Update both in Supabase
      const { error: error1 } = await supabase
        .from('watchlists_20240520')
        .update({ instruments: updatedFromInstruments })
        .eq('id', fromWatchlistId);

      const { error: error2 } = await supabase
        .from('watchlists_20240520')
        .update({ instruments: updatedToInstruments })
        .eq('id', toWatchlistId);

      if (error1 || error2) throw error1 || error2;

      // Update local state
      setWatchlists(prev => prev.map(w => {
        if (w.id === fromWatchlistId) return { ...w, instruments: updatedFromInstruments };
        if (w.id === toWatchlistId) return { ...w, instruments: updatedToInstruments };
        return w;
      }));
      
      return true;
    } catch (err) {
      console.error("Error moving instrument:", err);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const deleteWatchlist = async (id) => {
    try {
      const { error } = await supabase
        .from('watchlists_20240520')
        .delete()
        .eq('id', id);
      if (error) throw error;
      setWatchlists(prev => prev.filter(w => w.id !== id));
    } catch (err) {
      console.error("Error deleting watchlist:", err);
    }
  };

  const dateKey = format(currentDate, 'yyyy-MM-dd');

  const fetchDailyData = async () => {
    if (!session?.user?.id) return;
    try {
      const { data: todayRow, error } = await supabase
        .from('daily_plans_20240520')
        .select('data')
        .eq('plan_date', dateKey)
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (error) throw error;
      const baseDefault = getDefaultDailyData();

      if (todayRow?.data) {
        const merged = { ...baseDefault, ...todayRow.data, inFocusItems: todayRow.data.inFocusItems || [] };
        setCurrentDaily(merged);
        dailyDataRef.current = merged;
      } else {
        const prevDateKey = format(subDays(currentDate, 1), 'yyyy-MM-dd');
        const { data: prevRow } = await supabase
          .from('daily_plans_20240520')
          .select('data')
          .eq('plan_date', prevDateKey)
          .eq('user_id', session.user.id)
          .maybeSingle();

        let initialData = baseDefault;
        if (prevRow?.data?.inFocusItems) {
          const carriedItems = prevRow.data.inFocusItems
            .filter(item => item.carryOver)
            .map(item => ({ ...item, todayPlan: '', tradeLog: [], chartImageBase64: '', chartCaption: '' }));
          initialData = { ...baseDefault, inFocusItems: carriedItems };
        }
        setCurrentDaily(initialData);
        dailyDataRef.current = initialData;
      }
    } catch (err) {
      console.error("Error fetching daily data:", err);
    }
  };

  useEffect(() => {
    if (session && !loading) fetchDailyData();
  }, [dateKey, session, loading]);

  const updateCurrentDaily = useCallback((updater) => {
    if (!session?.user?.id) return;
    setCurrentDaily(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      dailyDataRef.current = next;

      clearTimeout(window.saveTimer);
      window.saveTimer = setTimeout(async () => {
        setIsSaving(true);
        try {
          await supabase.from('daily_plans_20240520').upsert({
            user_id: session.user.id,
            plan_date: dateKey,
            data: next
          }, { onConflict: 'user_id,plan_date' });
        } catch (err) {
          console.error("Failed to save daily plan:", err);
        } finally {
          setIsSaving(false);
        }
      }, 1000);

      return next;
    });
  }, [session, dateKey]);

  return (
    <AppContext.Provider value={{
      session, profile, setProfile, fetchWatchlists, watchlists, saveWatchlist, moveInstrument, deleteWatchlist,
      currentDate, setCurrentDate, dateKey, currentDaily, updateCurrentDaily, loading, isSaving
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppData = () => useContext(AppContext);